import { pluralize } from "core/strings";
import { add as DFAdd, differenceInHours, differenceInMinutes, differenceInSeconds, format, startOfDay, set } from "date-fns";
import { type FormatOptions } from "date-fns";

type ValidDate = Date | string;

type FormatDate = ValidDate | null;

const _format = (date: FormatDate = null, fmt: string, options?: FormatOptions) => !date ? null : format(date, fmt, options);

const monthDay = (date: FormatDate) => _format(date, "MMM d");
const standard = (date?: FormatDate) => _format(date, "MMM d, y");
const brief = (date?: FormatDate) => _format(date, "MM/dd/yy");

const dynamic = (seconds: number) => { /* change from hours to seconds */
	if (seconds === 0) return { value: "0 seconds", type: "second" };
	if (!seconds) return null;

	const _threshold = {
		minute: 60,
		hour: 3600,
		day: 86400,
		week: 604800,
		month: 2592000
	};

	if (seconds < _threshold.minute) return ({ value: pluralize(seconds, "second"), type: "second" }); // seconds
	if (seconds < _threshold.hour) return ({ value: pluralize(Math.round(seconds / _threshold.minute), "minute"), type: "minute" }); // minutes
	if (seconds < _threshold.day) return ({ value: pluralize(Math.round(seconds / _threshold.hour), "hour"), type: "hour" }); // hours
	if (seconds < _threshold.week) return ({ value: pluralize(Math.round(seconds / _threshold.day), "day"), type: "day" }); // days
	if (seconds < _threshold.month) return ({ value: pluralize(Math.round(seconds / _threshold.week), "week"), type: "week" }); // weeks

	return ({ value: pluralize(Math.round(seconds / _threshold.month), "month"), type: "month" }); // months
};

const add = {
	minutes: (date: ValidDate, amount: number) => DFAdd(date, { minutes: amount }),
	hours: (date: ValidDate, amount: number) => DFAdd(date, { hours: amount }),
	days: (date: ValidDate, amount: number) => DFAdd(date, { days: amount })
};

const hours = {
	toDays: (hours: number) => pluralize(Math.round(hours / 24), "day"),
	difference: (later: ValidDate, earlier: ValidDate = new Date()) => differenceInHours(later, earlier)
};

const minutes = {
	difference: (later: ValidDate, earlier: ValidDate = new Date()) => differenceInMinutes(later, earlier)
};

const seconds = {
	dynamic: (later: ValidDate, earlier: ValidDate = new Date()) => {
		const diff = differenceInSeconds(later, earlier);
		return dynamic(diff);
	},
	difference: (later: ValidDate, earlier: ValidDate = new Date()) => differenceInSeconds(later, earlier)
};

const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export default { add, brief, standard, startOfDay, minutes, monthDay, hours, seconds, set, getTimezone };